import tenantTheme from '@theme';
import tenantUtils from '@utils';
import tenantConstants from '@constants';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { useSelector } from 'react-redux';
import { Flex, Icon } from '../../../../components/common';
import { regaInformationClickEvent, regaQRCodeClickEvent } from '../../../../services/analyticsService';
import { formatNumberString, hasRegaDetailsContent, isHttpUrl } from '../../../../utility/utility';
import ListingDetailModal from './listingDetailModal';
import Styled from 'styled-components';

const SEE_MORE_ICON = `${process.env.PUBLIC_URL || ''}/profolio-assets/images/post-listing/rega-see-more-icon.svg`;

const Card = Styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  padding: 20px 20px 24px;
  border-radius: 12px;
  border: 1px solid #E1F2F0;
  background: #FFFFFF;
  width: 100%;
  cursor: pointer;
  user-select: none;
  transition: box-shadow 0.2s ease;

  &:hover { box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08); }

  ${(props) =>
    props.$mobile &&
    `
    width: auto;
    margin: 0 16px;
    padding: 16px;
    border-radius: 6px;
    border: 1px solid #ECECEC;
    background: linear-gradient(180deg, #FFFFFF 77.11%, #F2FAFA 100%);
  `}
`;

const Title = Styled.p`
  color: #222222;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  margin: 0;
`;

const GrayBox = Styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 0;
  padding: 12px 20px;
  border-radius: 6px;
  background: #FAFAFA;

  .label { color: #626262; font-size: 12px; font-weight: 500; line-height: 16px; margin: 0; }
  .value { color: #222222; font-size: 14px; font-weight: 600; line-height: 20px; margin: 0;
           white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  ${(props) =>
    props.$mobile &&
    `
    .value { font-size: 12px; line-height: 16px; }
  `}
`;

const InlineField = Styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;

  .label { color: #707070; font-size: 12px; font-weight: 500; line-height: 18px; margin: 0; flex-shrink: 0; }
  .value { color: #222222; font-size: 14px; font-weight: 600; line-height: 20px; margin: 0;
           display: flex; align-items: center; gap: 4px; }
`;

/* QR code + Go to REGA combined into one card per Figma */
const RegaQrCard = Styled.a`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 88px;
  border: 1px solid #F0F0F0;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  text-decoration: none !important;

  .qr-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 8px 6px;
    background: #FFFFFF;
  }

  .goto-rega {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 5px 6px 6px;
    background: #F2FAFA;
    color: #006169;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
  }
`;

const Divider = Styled.div`
  width: 100%;
  height: 1px;
  background: #E1F2F0;
`;

const SeeMore = Styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: #006169;
  font-size: 16px;
  font-weight: 700;
`;

/* ── Mobile-only pieces (Figma mobile spec) ── */
const SpecsRow = Styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 4px;
`;

const SpecStack = Styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  .label { color: #707070; font-size: 12px; font-weight: 500; line-height: 18px; margin: 0; white-space: nowrap; }
  .value { color: #222222; font-size: 14px; font-weight: 600; line-height: 21px; margin: 0;
           white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

/* 46px vertical rule sitting between adjacent property-spec stacks. */
const SpecSep = Styled.div`
  width: 1px;
  height: 46px;
  background: #ECECEC;
  flex-shrink: 0;
  align-self: center;
`;

const SoftDivider = Styled.div`
  width: 100%;
  height: 1px;
  background: #F2FAFA;
`;

const MobileFooter = Styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const MobileLocation = Styled.span`
  color: #222222;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SeeMoreText = Styled.span`
  color: #006169;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
`;

const RegaCard = ({ listing }) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const { user } = useSelector((state) => state.app.loginUser);
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);

  const regaLocation = useMemo(
    () =>
      [
        listing?.rega_details?.location?.district,
        listing?.rega_details?.location?.city,
        listing?.rega_details?.location?.region,
      ]
        .filter(Boolean)
        .join(', '),
    [listing?.rega_details?.location],
  );

  if (!hasRegaDetailsContent(listing?.rega_details)) return null;

  const specs = listing?.rega_details?.property_specs;
  const propertyType = tenantUtils.getLocalisedString(specs, 'listing_type');
  const purpose = tenantUtils.getLocalisedString(specs, 'advertisement_type');
  const price = specs?.price ? formatNumberString(specs.price, { maximumFractionDigits: 0 }) : null;
  const area = specs?.area_size ? `${specs.area_size} ${t('Sq. M.')}` : null;
  const adLicense = listing?.rega_details?.license_info?.ad_license_number;
  // CR Number is sourced from `legal_id` (sent at the edit-response root).
  const crNumber = listing?.legal_id;
  const regaUrl = listing?.rega_details?.url;

  const specItems = [
    propertyType && { label: t('Property Type'), value: propertyType },
    purpose && { label: t('Purpose'), value: purpose },
    price && {
      label: t('Price'),
      value: (
        <>
          {tenantConstants.CURRENCY_SYMBOL()} {price}
        </>
      ),
    },
    area && { label: t('Area'), value: area },
  ].filter(Boolean);

  const handleClick = () => {
    setModalVisible(true);
    regaInformationClickEvent(user);
  };

  const stop = (e) => e.stopPropagation();

  return (
    <>
      <Card
        $mobile={isMobile}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleClick();
        }}
      >
        <Flex align="center" gap="8px">
          <Title>{t('Listing Details from REGA')}</Title>
          <Icon icon="PiSealCheckFill" color="#3E7BFA" size={16} />
        </Flex>

        {(adLicense || crNumber) && (
          <Flex gap="6px" align="stretch">
            {adLicense && (
              <GrayBox $mobile={isMobile}>
                <p className="label">{t('Ad License')}</p>
                <p className="value">{adLicense}</p>
              </GrayBox>
            )}
            {crNumber && (
              <GrayBox $mobile={isMobile}>
                <p className="label">{t('CR Number')}</p>
                <p className="value">{crNumber}</p>
              </GrayBox>
            )}
          </Flex>
        )}

        {isMobile ? (
          /* ── Mobile: property specs in a horizontal row of vertical stacks, no QR ── */
          <>
            {specItems.length > 0 && (
              <SpecsRow>
                {specItems.map((s, i) => (
                  <React.Fragment key={s.label}>
                    {i > 0 && <SpecSep />}
                    <SpecStack>
                      <p className="label">{s.label}</p>
                      <p className="value">{s.value}</p>
                    </SpecStack>
                  </React.Fragment>
                ))}
              </SpecsRow>
            )}

            <SoftDivider />

            <MobileFooter>
              {regaLocation && (
                <Flex align="center" gap="4px" style={{ minWidth: 0 }}>
                  <Icon icon="FaMapMarkerAlt" color={tenantTheme['primary-color']} size={12} />
                  <MobileLocation>{regaLocation}</MobileLocation>
                </Flex>
              )}
              <SeeMoreText>{t('See More Details')}</SeeMoreText>
            </MobileFooter>
          </>
        ) : (
          /* ── Desktop: specs + QR, location, divider, centered See More ── */
          <>
            <Flex justify="space-between" align="flex-start" gap="12px">
              <Flex vertical gap="8px" style={{ minWidth: 0 }}>
                {propertyType && (
                  <InlineField>
                    <p className="label">{t('Property Type')}</p>
                    <p className="value">{propertyType}</p>
                  </InlineField>
                )}
                {purpose && (
                  <InlineField>
                    <p className="label">{t('Purpose')}</p>
                    <p className="value">{purpose}</p>
                  </InlineField>
                )}
                {price && (
                  <InlineField>
                    <p className="label">{t('Price')}</p>
                    <p className="value">
                      {tenantConstants.CURRENCY_SYMBOL()} {price}
                    </p>
                  </InlineField>
                )}
                {area && (
                  <InlineField>
                    <p className="label">{t('Area')}</p>
                    <p className="value">{area}</p>
                  </InlineField>
                )}
              </Flex>

              {isHttpUrl(regaUrl) && (
                <RegaQrCard
                  href={regaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    stop(e);
                    regaQRCodeClickEvent(user, false, listing?.rega_details?.license_info);
                  }}
                >
                  <div className="qr-wrap">
                    <QRCode size={60} value={regaUrl} viewBox="0 0 256 256" />
                  </div>
                  <div className="goto-rega">
                    {t('Go to REGA')}
                    <Icon icon="MdArrowOutward" size={10} color="#006169" />
                  </div>
                </RegaQrCard>
              )}
            </Flex>

            {regaLocation && (
              <Flex align="center" gap="8px">
                <Icon icon="FaMapMarkerAlt" color={tenantTheme['primary-color']} size={14} />
                <span style={{ color: '#222222', fontSize: 14, fontWeight: 600, lineHeight: '20px' }}>
                  {regaLocation}
                </span>
              </Flex>
            )}

            <Divider />

            <SeeMore>
              <img
                src={SEE_MORE_ICON}
                alt=""
                width={13}
                height={18}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              {t('See More Details')}
            </SeeMore>
          </>
        )}
      </Card>
      <ListingDetailModal visible={modalVisible} setModalVisible={setModalVisible} listing={listing} />
    </>
  );
};

export default RegaCard;
